import { useState, useEffect, useMemo, useCallback } from 'react';
import {
    useNodesState,
    useEdgesState,
    addEdge,
    applyNodeChanges,
    applyEdgeChanges,
    Node,
    Edge,
    Connection,
    NodeChange,
    EdgeChange,
    ReactFlowInstance,
    NodeDragHandler,
} from 'reactflow';
import type { PolicyTemplate, Condition, NestedCondition, CountCondition, NotCondition, PolicyRuleIf } from '../../../../types/policy';
import { policyToFlowElements, getLayoutedElements } from '../utils/graphUtils';
import {
    deleteNodeAndChildrenFromPolicy,
    addNodeToPolicy,
    updateNodeDataInPolicy,
    toggleGroupTypeInPolicy,
    toggleNotInPolicy,
    setRootNodeInPolicy
} from '../utils/policyUtils';
import type { GraphViewContextType } from '../types/graph'; // For context handlers type

// Type for the add target context (moved from GraphView)
interface AddTargetContext {
    type: 'group' | 'countWhere';
    path: (string | number)[];
}

interface UsePolicyGraphInteractionsProps {
  policy: PolicyTemplate;
  onUpdate: (updatedPolicy: PolicyTemplate) => void;
  rfInstance: ReactFlowInstance | null;
}

interface UsePolicyGraphInteractionsReturn {
  nodes: Node[];
  edges: Edge[];
  onNodesChange: (changes: NodeChange[]) => void;
  onEdgesChange: (changes: EdgeChange[]) => void;
  onConnect: (connection: Connection) => void;
  onNodeClick: (event: React.MouseEvent, node: Node) => void;
  onPaneClick: () => void;
  contextHandlers: GraphViewContextType;
  handleManualLayout: () => void;
  setHasUserDragged: React.Dispatch<React.SetStateAction<boolean>>;
  editingNodePath: (string | number)[] | null;
  conditionToEdit: Condition | NestedCondition | NotCondition | CountCondition | null;
  onNodeDragStop: NodeDragHandler;
  handleEditorUpdate: (updatedData: Partial<Condition | CountCondition | NestedCondition | NotCondition>) => void;
}

export const usePolicyGraphInteractions = ({
  policy,
  onUpdate,
  rfInstance,
}: UsePolicyGraphInteractionsProps): UsePolicyGraphInteractionsReturn => {

  // Core React Flow state management
  const [nodes, setNodes, onNodesChangeInternal] = useNodesState([]);
  const [edges, setEdges, onEdgesChangeInternal] = useEdgesState([]);

  // State for tracking where a new node should be added (moved from GraphView)
  const [targetAddContext, setTargetAddContext] = useState<AddTargetContext | null>(null);

  // Layout state (moved from useGraphLayout)
  const [hasUserDragged, setHasUserDragged] = useState<boolean>(false);

  // State to track which subtrees are collapsed (serialized paths)
  const [collapsedPaths, setCollapsedPaths] = useState<Set<string>>(new Set());

  // Editor state is now managed within this hook
  const [editingNodePath, setEditingNodePath] = useState<(string | number)[] | null>(null);

  // --- Define Interaction Handlers (using imported policy utils) ---
  const handleDeleteNode = useCallback((path: (string | number)[]) => {
    if (!path || path.length === 0) {
      onUpdate(setRootNodeInPolicy(policy, {} as NestedCondition));
      setEditingNodePath(null);
      return;
    }
    onUpdate(deleteNodeAndChildrenFromPolicy(policy, path));
    setEditingNodePath(null);
  }, [policy, onUpdate, setEditingNodePath]);

  const handleAddCondition = useCallback((parentPath: (string | number)[]) => {
    console.log('[usePolicyGraphInteractions] handleAddCondition invoked with parentPath:', parentPath);
    onUpdate(addNodeToPolicy(policy, parentPath, { field: '', equals: '' }));
  }, [policy, onUpdate]);

  const handleAddAllOfGroup = useCallback((parentPath: (string | number)[]) => {
    onUpdate(addNodeToPolicy(policy, parentPath, { allOf: [] }));
  }, [policy, onUpdate]);

  const handleAddAnyOfGroup = useCallback((parentPath: (string | number)[]) => {
    onUpdate(addNodeToPolicy(policy, parentPath, { anyOf: [] }));
  }, [policy, onUpdate]);

  const handleAddCountCondition = useCallback((parentPath: (string | number)[]) => {
    const initialCountCondition: CountCondition = {
      count: {
        field: "",
        where: {}
      },
      greater: 0
    };
    onUpdate(addNodeToPolicy(policy, parentPath, initialCountCondition));
  }, [policy, onUpdate]);

  const handleToggleGroupType = useCallback((path: (string | number)[]) => {
    onUpdate(toggleGroupTypeInPolicy(policy, path));
  }, [policy, onUpdate]);

  const handleToggleNot = useCallback((path: (string | number)[]) => {
    onUpdate(toggleNotInPolicy(policy, path));
  }, [policy, onUpdate]);

  // This handler just sets state managed by the parent (GraphView)
  const handleEditNode = useCallback((path: (string | number)[]) => {
    setEditingNodePath(path);
  }, [setEditingNodePath]);

  const handleInitRoot = useCallback((type: 'condition' | 'group' | 'count') => {
    let newNode: Condition | NestedCondition | CountCondition;
    if (type === 'condition') {
      newNode = { field: '', equals: '' };
    } else if (type === 'group') {
      newNode = { allOf: [] };
    } else {
      newNode = {
        count: {
          field: '',
          where: {}
        },
        greater: 0
      } as CountCondition;
    }
    onUpdate(setRootNodeInPolicy(policy, newNode));
  }, [policy, onUpdate]);

  // handleEditorUpdate now defined inside the hook
  const handleEditorUpdate = useCallback((updatedConditionData: Partial<Condition | CountCondition | NestedCondition | NotCondition>) => {
    if (!editingNodePath) return;
    onUpdate(updateNodeDataInPolicy(policy, editingNodePath, updatedConditionData));
  }, [policy, editingNodePath, onUpdate]);

  // --- Create Handler Object for Context ---
  const contextHandlers: GraphViewContextType = useMemo(() => ({
    handleDeleteNode,
    handleAddCondition,
    handleAddAllOfGroup,
    handleAddAnyOfGroup,
    handleAddCountCondition,
    handleToggleGroupType,
    handleToggleNot,
    onEditNode: handleEditNode, // Renamed for clarity in context
    handleInitRoot,
    handleToggleCollapse: (path) => {
      const key = JSON.stringify(path);
      setCollapsedPaths(prev => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      });
    },
    collapsedPaths: Array.from(collapsedPaths)
  }), [
    handleDeleteNode,
    handleAddCondition,
    handleAddAllOfGroup,
    handleAddAnyOfGroup,
    handleAddCountCondition,
    handleToggleGroupType,
    handleToggleNot,
    handleEditNode,
    handleInitRoot,
    collapsedPaths
  ]);

  // --- Internal Layout Application Function ---
  const applyLayoutInternal = useCallback((nodesToLayout: Node[], edgesToLayout: Edge[], shouldFitView: boolean = false) => {
    if (nodesToLayout.length === 0) {
        setNodes([]); // Use internal setter
        setEdges([]); // Use internal setter
        setHasUserDragged(false);
        return;
    }
    console.log(`Applying auto layout (internal)... ${shouldFitView ? 'and fitting view' : '(no fit view)'}`);
    const nodesWithCopiedData = nodesToLayout.map(n => ({ ...n, data: { ...n.data } }));
    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
        nodesWithCopiedData,
        edgesToLayout
    );

    // Filter out nodes/edges hidden by collapsed paths
    const isDescendantOfCollapsed = (path: any[]) => {
      for (const key of Array.from(collapsedPaths)) {
        const collapsed = JSON.parse(key);
        if (path.length > collapsed.length && collapsed.every((seg: any, i: number) => path[i] === seg)) {
          return true;
        }
      }
      return false;
    };
    const filteredNodes = layoutedNodes.filter(n => {
      const path = n.data?.path;
      return !(Array.isArray(path) && isDescendantOfCollapsed(path));
    });
    const nodeIds = new Set(filteredNodes.map(n => n.id));
    const filteredEdges = layoutedEdges.filter(e => nodeIds.has(e.source) && nodeIds.has(e.target));
    setNodes(filteredNodes);
    setEdges(filteredEdges);
    setHasUserDragged(false);

    if (shouldFitView && rfInstance) {
        requestAnimationFrame(() => {
            rfInstance.fitView({ padding: 0.1, duration: 300 });
        });
    }
  }, [rfInstance, setNodes, setEdges, collapsedPaths]); // Added collapsedPaths to dependencies

  // --- Effect to Recalculate Nodes/Edges and Apply Layout on Policy Change ---
  useEffect(() => {
    console.log("Policy changed (hook), recalculating flow elements...");
    const ruleIf = policy.properties?.policyRule?.if;

    if (!ruleIf || Object.keys(ruleIf).length === 0) {
      console.log("Policy rule 'if' is empty or undefined, applying empty layout.");
      applyLayoutInternal([], [], false); // Use internal layout function
      return;
    }

    const { nodes: calculatedNodes, edges: calculatedEdges } = policyToFlowElements(ruleIf);
    console.log(`Generated ${calculatedNodes.length} nodes and ${calculatedEdges.length} edges.`);
    // Apply layout using the internal function
    applyLayoutInternal(calculatedNodes, calculatedEdges, false);

  }, [policy, applyLayoutInternal]); // Depend on policy and the internal layout function

  // --- Manual Layout Handler ---
  const handleManualLayout = useCallback(() => {
    console.log("Manual re-layout triggered (hook).");
    // Pass the current nodes/edges state to the internal layout function
    applyLayoutInternal(nodes, edges, true); 
  }, [nodes, edges, applyLayoutInternal]); // Depend on current state and internal layout func


  const onConnect = useCallback(
    (connection: Connection) => setEdges((eds) => addEdge(connection, eds)),
    [setEdges]
  );

  // --- Click Handlers ---
  const onNodeClick = useCallback((event: React.MouseEvent, node: Node) => {
      if (node.type === 'group') { return; } // Prevent editor opening for group nodes
      console.log("Node clicked (hook):", node.id, "Data:", node.data);
      if (node.data?.path && (node.data?.isCondition || node.data?.isCount)) {
          setEditingNodePath(node.data.path);
          console.log("Set editing path (hook):", node.data.path);
      } else {
          setEditingNodePath(null);
      }
      // We might need targetAddContext later if adding nodes directly on graph is implemented
      // setTargetAddContext({ type: 'group', path: node.data?.path ?? [] });
  }, [setEditingNodePath]);

  const onPaneClick = useCallback(() => {
      setEditingNodePath(null);
      // setTargetAddContext({ type: 'group', path: [] }); // Reset target context to root
  }, [setEditingNodePath]);

  // --- Node Drag Stop Handler (moved here) ---
   const onNodeDragStop = useCallback<NodeDragHandler>((event, node, nodes) => {
        setHasUserDragged(true); // Use internal setter from this hook
        console.log(`Node drag stopped (hook): ${node.id}`);
    }, [setHasUserDragged]); // Use internal setter dependency

  // --- Get Condition for Editor (moved here) ---
   const getConditionForEditor = useCallback((): Condition | NestedCondition | NotCondition | CountCondition | null => {
        if (!editingNodePath) return null;
        try {
            let current: any = policy.properties?.policyRule?.if;
            if (!current) return null; 
            for (const segment of editingNodePath) { 
                if (current === undefined || current === null || typeof current !== 'object' || !(segment in current)) { 
                    console.error("(Hook) Error getting condition for editor: Invalid path segment", segment, "in path", editingNodePath);
                    return null; 
                } 
                current = current[segment]; 
            } 
            if (typeof current === 'object' && current !== null) { 
                return current as Condition | NestedCondition | NotCondition | CountCondition;
            } else {
                console.error("(Hook) Error getting condition for editor: Path did not resolve to an object", editingNodePath, "resolved to:", current);
                return null;
            }
        } catch (error) {
            console.error("(Hook) Error getting condition for editor:", error, "Path:", editingNodePath);
            return null;
        }
    }, [policy, editingNodePath]); // Depends on policy and internal editingNodePath

  // Calculate conditionToEdit based on internal state
  const conditionToEdit = getConditionForEditor();

  return {
    nodes,
    edges,
    onNodesChange: onNodesChangeInternal, // Return the internal handlers from useNodesState/useEdgesState
    onEdgesChange: onEdgesChangeInternal,
    onConnect,
    onNodeClick,
    onPaneClick,
    contextHandlers,
    handleManualLayout,
    setHasUserDragged,
    editingNodePath,
    conditionToEdit,
    onNodeDragStop,
    handleEditorUpdate,
  };
}; 